/* global Office */

/**
 * useOfficeDarkMode - Reflects the Outlook UI theme.
 *
 * Priority:
 *  1. `Office.context.officeTheme.bodyBackgroundColor` (luminance < 0.5 => dark)
 *  2. `prefers-color-scheme: dark` (system / webview fallback)
 *
 * Reacts to `Office.EventType.OfficeThemeChanged` (Outlook 1.14+)
 * and to OS color-scheme changes.
 */

import { useEffect, useState } from "react";

function hexToLuminance(hex) {
  if (typeof hex !== "string") return 1;
  const clean = hex.replace("#", "");
  if (clean.length < 6) return 1;
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function detectDark() {
  const bg = window.Office?.context?.officeTheme?.bodyBackgroundColor;
  if (bg) return hexToLuminance(bg) < 0.5;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

export function useOfficeDarkMode() {
  const [isDark, setIsDark] = useState(detectDark);

  useEffect(() => {
    const sync = () => setIsDark(detectDark());

    const mailbox = window.Office?.context?.mailbox;
    const themeEvent = window.Office?.EventType?.OfficeThemeChanged;
    if (mailbox?.addHandlerAsync && themeEvent) {
      try { mailbox.addHandlerAsync(themeEvent, sync); } catch { /* unsupported host */ }
    }

    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    mq?.addEventListener?.("change", sync);

    return () => {
      if (mailbox?.removeHandlerAsync && themeEvent) {
        try { mailbox.removeHandlerAsync(themeEvent); } catch { /* ignore */ }
      }
      mq?.removeEventListener?.("change", sync);
    };
  }, []);

  return isDark;
}
